import {
  KillChainCategory,
  ScoringBand,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseDashboardStageHeatmapRowDto,
  type StudyRunCaseDashboardThreadVerdictDto,
} from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Tile } from '@components/shared/tile.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { GavelIcon } from 'lucide-react';
import type { FC } from 'react';
import {
  type BriefRiskItem,
  type BriefStudyVerdict,
  buildBriefRecommendation,
  getBriefAggregateStats,
} from './meia-p5-brief.util';

type BriefRecommendationTileProps = {
  activeMission: StudyRunCaseDashboardMissionDto | null;
  activeThreadVerdict: StudyRunCaseDashboardThreadVerdictDto | null;
  briefVerdict: BriefStudyVerdict;
  mode?: 'aggregate' | 'selected';
  risks: BriefRiskItem[];
  stages: StudyRunCaseDashboardStageHeatmapRowDto[];
};

export const BriefRecommendationTile: FC<BriefRecommendationTileProps> = (props) => {
  const { activeMission, activeThreadVerdict, briefVerdict, mode = 'selected', risks, stages } = props;
  const weakestStageName = getWeakestStageName(stages);
  const threadName = activeThreadVerdict
    ? `${activeThreadVerdict.threadName} (${getDashboardKillChainLabel(activeThreadVerdict.killChainCategory)})`
    : null;
  const stats = getBriefAggregateStats(stages);
  const recommendation = buildBriefRecommendation({
    activeMissionName: activeMission?.missionName,
    activeThreadName: threadName,
    band: briefVerdict.band,
    mode,
    riskCount: risks.length,
    totalStages: stats.totalStages,
    totalTasks: stats.totalTasks,
    weakestStageName,
  });

  return (
    <Tile
      contentClassName="gap-4"
      header={{
        title: (
          <span className="inline-flex items-center gap-2">
            <GavelIcon className="size-4 text-primary" />
            Recommendation to Decision Maker
          </span>
        ),
      }}
    >
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="text-sm leading-6 text-gray-800">{recommendation}</p>
          </div>
          <Badge
            className="shrink-0"
            variant={recommendationBadgeVariant(briefVerdict.band)}
          >
            {recommendationActionLabel(briefVerdict.band)}
          </Badge>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <BriefStatTile
          label="Stages Green"
          tone="green"
          value={`${stats.greenStages}/${stats.totalStages}`}
        />
        <BriefStatTile
          label="Tasks Total"
          value={`${stats.totalTasks}`}
        />
        <BriefStatTile
          label="Open Risks"
          tone={risks.length > 0 ? 'yellow' : 'green'}
          value={`${risks.length}`}
        />
      </div>
    </Tile>
  );
};

type BriefStatTileProps = {
  label: string;
  tone?: 'default' | 'green' | 'yellow';
  value: string;
};

const BriefStatTile: FC<BriefStatTileProps> = (props) => {
  const { label, tone = 'default', value } = props;

  return (
    <div className="rounded-md border border-gray-200 px-3 py-2">
      <div
        className={cn(
          'text-2xl font-semibold',
          tone === 'green' && 'text-emerald-600',
          tone === 'yellow' && 'text-amber-600',
          tone === 'default' && 'text-gray-950',
        )}
      >
        {value}
      </div>
      <BlueLabel
        className="text-xs"
        text={label}
      />
    </div>
  );
};

const dashboardKillChainLabels: Record<KillChainCategory, string> = {
  [KillChainCategory.C2]: 'C2',
  [KillChainCategory.COUNTER_UAS]: 'Counter-UAS',
  [KillChainCategory.CUSTOM]: 'Custom',
  [KillChainCategory.CYBER]: 'Cyber',
  [KillChainCategory.D3A]: 'D3A',
  [KillChainCategory.F2T2EA]: 'F2T2EA',
  [KillChainCategory.ISR]: 'ISR',
  [KillChainCategory.LOGISTICS]: 'Logistics',
};

const getDashboardKillChainLabel = (value: KillChainCategory): string => dashboardKillChainLabels[value];

const getWeakestStageName = (stages: StudyRunCaseDashboardStageHeatmapRowDto[]): string | null => {
  const sortedStages = [...stages].sort(
    (a, b) => (a.compositeScore ?? Number.MAX_SAFE_INTEGER) - (b.compositeScore ?? Number.MAX_SAFE_INTEGER),
  );
  return sortedStages[0]?.stageName ?? null;
};

const recommendationActionLabel = (band?: ScoringBand | null): string => {
  switch (band) {
    case ScoringBand.GREEN:
      return 'Proceed to G4';
    case ScoringBand.YELLOW:
      return 'Conditional Review';
    case ScoringBand.RED:
      return 'Return to P3/P4';
    case ScoringBand.PENDING:
    case null:
    case undefined:
      return 'Await Scoring';
  }
};

const recommendationBadgeVariant = (band?: ScoringBand | null): BadgeProps['variant'] => {
  switch (band) {
    case ScoringBand.GREEN:
      return 'success';
    case ScoringBand.RED:
      return 'destructive';
    case ScoringBand.YELLOW:
      return 'warning';
    case ScoringBand.PENDING:
    case null:
    case undefined:
      return 'pending';
  }
};
